import { type ModuleCode, type Permission } from '@ibe/constants';
import { EmptyState } from '@ibe/ui';
import { ShieldOff } from 'lucide-react';
import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { AUTH_STATUS, useAuth } from './auth-context.js';

/**
 * Hides UI a user has no permission for.
 *
 * This is presentation, not enforcement. Every guarded endpoint checks the
 * same permission server-side and it is that check which decides. A control
 * hidden here and unguarded there is still reachable by anyone who types the
 * URL — the guard exists so people are not shown things that will fail, not
 * so the API can skip checking.
 *
 * By default it renders nothing. That is right for a button in a toolbar and
 * wrong for a whole page, so `fallback` exists for the second case: a blank
 * screen with no explanation is indistinguishable from a broken one, and the
 * support call it produces is "the page is empty".
 */
export interface PermissionGuardProps {
  readonly permission: Permission | readonly Permission[];
  /** Require every permission rather than any of them. */
  readonly requireAll?: boolean;
  /** Narrows the check to one property's scope. */
  readonly propertyId?: string;
  readonly fallback?: ReactNode;
  readonly children: ReactNode;
}

export function PermissionGuard({
  permission,
  requireAll = false,
  propertyId,
  fallback = null,
  children,
}: PermissionGuardProps): React.JSX.Element | null {
  const auth = useAuth();

  const permissions = Array.isArray(permission)
    ? (permission as readonly Permission[])
    : [permission as Permission];

  const allowed =
    propertyId === undefined
      ? requireAll
        ? auth.canAll(permissions)
        : auth.canAny(permissions)
      : permissions
          .map((entry) => auth.can(entry, propertyId))
          .reduce((left, right) => (requireAll ? left && right : left || right), requireAll);

  if (!allowed) return <>{fallback}</>;
  return <>{children}</>;
}

/**
 * Hides UI belonging to a module the tenant has not bought.
 *
 * A separate guard from `PermissionGuard` because it answers a different
 * question. A permission is what this person may do; a module is what their
 * organisation bought. Someone can hold `Property.Update` in full and still
 * have nothing to type into a PMS field, because their tenant runs no PMS —
 * and a field that can only ever stay empty is worse than an absent one. It
 * invites a question ("what do I put here?") that has no answer.
 *
 * Presentation only, as ever. The server caps a tenant's permissions by its
 * modules at login, and that cap is the enforcement.
 */
export interface ModuleGuardProps {
  readonly module: ModuleCode;
  readonly fallback?: ReactNode;
  readonly children: ReactNode;
}

export function ModuleGuard({
  module,
  fallback = null,
  children,
}: ModuleGuardProps): React.JSX.Element | null {
  const auth = useAuth();

  if (!auth.hasModule(module)) return <>{fallback}</>;
  return <>{children}</>;
}

/** The page-level fallback: says what happened rather than showing nothing. */
export function NotPermitted(): React.JSX.Element {
  return (
    <EmptyState
      icon={<ShieldOff className="h-8 w-8" />}
      title="You do not have access to this"
      description="Ask an administrator to grant you the permission this page needs."
    />
  );
}

/**
 * Requires a signed-in user, redirecting to the login page if there is none.
 *
 * While the session is still being restored it renders nothing rather than
 * redirecting. Redirecting during that window sends someone who followed a
 * link to a reservation to the login page, signs them in, and lands them on
 * the dashboard — the deep link is gone, and it looks like the link was
 * broken.
 */
export function RequireAuth({
  children,
}: {
  readonly children: ReactNode;
}): React.JSX.Element | null {
  const auth = useAuth();
  const location = useLocation();

  if (auth.status === AUTH_STATUS.RESTORING) return null;

  if (auth.status === AUTH_STATUS.MFA_ENROLL || auth.status === AUTH_STATUS.MFA_VERIFY) {
    return <Navigate to="/mfa" replace />;
  }

  if (auth.status === AUTH_STATUS.ANONYMOUS) {
    // The attempted URL rides along so the login page can return to it.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}
